/**
 * SEO — métadonnées par route + données structurées (Schema.org).
 * ---------------------------------------------------------------------------
 * Utilisé (1) par scripts/prerender.js pour injecter <title>, description,
 * canonical, Open Graph / Twitter et JSON-LD dans le HTML statique de chaque
 * route, et (2) par useHead() côté client lors de la navigation interne.
 *
 * Règles (cahier des charges §18, §21) : un seul H1 par page, canonical
 * absolue, aucune note/avis/nombre d'utilisateurs/partenaire dans les
 * données structurées — uniquement des faits vérifiés sur le site.
 */
import { useEffect } from 'react';
import { SITE_URL, CONTACT_EMAIL, FAQ } from './content';

const OG_IMAGE = `${SITE_URL}/og-civik.png`;
const DEFAULT_TITLE = "CIVIK – Application d'entraide et de sécurité citoyenne";
const DEFAULT_DESC = "CIVIK est une application d'entraide citoyenne qui permet de demander de l'aide, d'alerter autour de soi et de documenter une situation. Découvrez CIVIK pour les citoyens et les collectivités.";

const ORGANIZATION = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'CIVIK',
  alternateName: 'CIVIK — Entraide & sécurité citoyenne',
  slogan: 'Ensemble, nous sommes moins seuls.',
  description: "CIVIK est une application d'entraide et de sécurité citoyenne destinée aux citoyens et aux collectivités.",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-civik.png`,
  email: CONTACT_EMAIL,
  areaServed: { '@type': 'Country', name: 'France' },
  contactPoint: [{ '@type': 'ContactPoint', email: CONTACT_EMAIL, contactType: 'customer support', availableLanguage: 'French' }],
};

const WEBSITE = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'CIVIK',
  description: DEFAULT_DESC,
  inLanguage: 'fr-FR',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const SOFTWARE = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#app`,
  name: 'CIVIK',
  description: "Application d'entraide et de sécurité citoyenne qui permet de demander de l'aide, d'alerter autour de soi et de documenter une situation.",
  applicationCategory: 'UtilitiesApplication',
  operatingSystem: 'Android, iOS, Web',
  url: SITE_URL,
  inLanguage: 'fr-FR',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Les fonctions essentielles sont gratuites.' },
  // Audiences et usages (specs complémentaires §12) — strictement ceux affichés sur le site.
  audience: [
    { '@type': 'Audience', audienceType: 'Citoyens' },
    { '@type': 'Audience', audienceType: 'Collectivités territoriales' },
  ],
  featureList: ["Demander de l'aide", 'Alerter autour de soi', 'Documenter une situation', "Entraide de proximité"],
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const FAQ_LD = {
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/faq#faqpage`,
  mainEntity: FAQ.items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
};

function breadcrumb(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Accueil', url: `${SITE_URL}/` }, ...items].map((it, i) => ({
      '@type': 'ListItem', position: i + 1, name: it.name, item: it.url,
    })),
  };
}

function webPage(path, name, description, extra = {}) {
  return {
    '@type': 'WebPage',
    '@id': `${SITE_URL}${path}#webpage`,
    url: `${SITE_URL}${path}`,
    name,
    description,
    inLanguage: 'fr-FR',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#app` },
    ...extra,
  };
}

/** Une entrée par route publique prerendue. `breadcrumb` sert aussi au fil d'Ariane visuel. */
export const ROUTES = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    ld: [ORGANIZATION, WEBSITE, SOFTWARE, FAQ_LD],
  },
  '/comment-ca-marche': {
    title: 'Comment ça marche – CIVIK, application pour demander de l\'aide et alerter autour de soi',
    description: "Demander de l'aide, alerter, documenter, s'entraider : le fonctionnement de CIVIK, application d'entraide et de sécurité citoyenne, expliqué en quatre étapes simples.",
    breadcrumb: [{ name: 'Comment ça marche', url: `${SITE_URL}/comment-ca-marche` }],
  },
  '/entraide-citoyenne': {
    title: 'Entraide citoyenne de proximité – CIVIK, application d\'entraide citoyenne',
    description: "CIVIK est une application d'entraide citoyenne : un réseau d'entraide de proximité pour être informé, être utile et rester responsable, sans jamais se mettre en danger.",
    breadcrumb: [{ name: 'Entraide citoyenne', url: `${SITE_URL}/entraide-citoyenne` }],
  },
  '/securite-citoyenne': {
    title: 'Sécurité citoyenne et alerte – CIVIK, application de sécurité citoyenne',
    description: "Application de sécurité citoyenne et de sécurité personnelle : en cas de danger, CIVIK permet de déclencher une alerte citoyenne et de documenter la situation. CIVIK ne remplace ni la police, ni la gendarmerie, ni les secours.",
    breadcrumb: [{ name: 'Sécurité citoyenne', url: `${SITE_URL}/securite-citoyenne` }],
  },
  '/collectivites': {
    title: 'CIVIK pour les collectivités – sécurité citoyenne, prévention et tranquillité publique',
    description: "Solution d'entraide et de sécurité citoyenne pour les collectivités : réseau local d'entraide citoyenne, diffusion des alertes, remontées du terrain, participation citoyenne, en complément des dispositifs de prévention et de tranquillité publique.",
    breadcrumb: [{ name: 'Collectivités', url: `${SITE_URL}/collectivites` }],
  },
  '/police-municipale': {
    title: 'CIVIK et la police municipale – solution numérique de tranquillité publique',
    description: "Application d'entraide et de sécurité citoyenne pour la police municipale : CIVIK complète les politiques et outils locaux de prévention, de proximité et de tranquillité publique, sans se substituer aux services compétents.",
    breadcrumb: [{ name: 'Collectivités', url: `${SITE_URL}/collectivites` }, { name: 'Police municipale', url: `${SITE_URL}/police-municipale` }],
  },
  '/confidentialite-donnees': {
    title: 'Données personnelles et confidentialité – CIVIK',
    description: 'Transparence, maîtrise, minimisation, sécurité : comment CIVIK doit expliquer et protéger les données utilisées par l\'application (localisation, images, son).',
    breadcrumb: [{ name: 'Données & confidentialité', url: `${SITE_URL}/confidentialite-donnees` }],
  },
  '/faq': {
    title: 'Questions fréquentes – CIVIK',
    description: "CIVIK remplace-t-il le 17, le 18 ou le 112 ? Dois-je intervenir ? Ma position, mes vidéos, les collectivités : les réponses aux questions fréquentes sur l'application d'entraide et de sécurité citoyenne CIVIK.",
    breadcrumb: [{ name: 'FAQ', url: `${SITE_URL}/faq` }],
    ld: [FAQ_LD],
  },
  '/contact': {
    title: 'Contact – CIVIK',
    description: 'Contacter CIVIK : citoyens, professionnels et collectivités.',
    breadcrumb: [{ name: 'Contact', url: `${SITE_URL}/contact` }],
  },
  '/accessibilite': {
    title: 'Accessibilité – CIVIK',
    description: "Déclaration d'accessibilité du site civik.tech.",
    breadcrumb: [{ name: 'Accessibilité', url: `${SITE_URL}/accessibilite` }],
    noindex: false,
  },
  '/mentions-legales': { title: 'Mentions légales – CIVIK', description: 'Mentions légales du site civik.tech et de l\'application CIVIK.', breadcrumb: [{ name: 'Mentions légales', url: `${SITE_URL}/mentions-legales` }] },
  '/politique-confidentialite': { title: 'Politique de confidentialité – CIVIK', description: 'Politique de confidentialité de CIVIK : données traitées, finalités, durées, droits.', breadcrumb: [{ name: 'Politique de confidentialité', url: `${SITE_URL}/politique-confidentialite` }] },
  '/cgu': { title: "Conditions générales d'utilisation – CIVIK", description: "Conditions générales d'utilisation de l'application CIVIK et du site civik.tech.", breadcrumb: [{ name: 'CGU', url: `${SITE_URL}/cgu` }] },
  '/cgv': { title: 'Conditions générales de vente – CIVIK', description: 'Conditions générales de vente de l\'abonnement CIVIK.', breadcrumb: [{ name: 'CGV', url: `${SITE_URL}/cgv` }] },
  '/politique-cookies': { title: 'Politique de cookies – CIVIK', description: 'Le site civik.tech ne dépose aucun cookie publicitaire, analytique ou de mesure d\'audience.', breadcrumb: [{ name: 'Politique de cookies', url: `${SITE_URL}/politique-cookies` }] },
  '/404': { title: 'Page introuvable – CIVIK', description: 'Cette page n\'existe pas.', noindex: true },
};

/** Routes de l'espace utilisateur (application) : jamais indexées. */
export const APP_ROUTES_NOINDEX = ['/candidature', '/connexion', '/inscription', '/mot-de-passe-oublie', '/reinitialiser', '/verifier-email', '/mon-compte', '/urgence', '/suppression-compte'];

export function metaFor(path) {
  const m = ROUTES[path];
  if (m) return { path, ...m };
  if (APP_ROUTES_NOINDEX.includes(path)) return { path, title: 'CIVIK', description: DEFAULT_DESC, noindex: true, keepTitle: true };
  return { path: '/404', ...ROUTES['/404'], noindex: true };
}

export function jsonLdFor(path) {
  const m = metaFor(path);
  const graph = [];
  if (path === '/') graph.push(...m.ld);
  else {
    graph.push(webPage(path, m.title, m.description));
    if (m.breadcrumb) graph.push(breadcrumb(m.breadcrumb));
    if (m.ld) graph.push(...m.ld);
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Balises <head> complètes d'une route (prerender). */
export function headTagsFor(path) {
  const m = metaFor(path);
  const url = `${SITE_URL}${m.path === '/404' ? path : m.path}`;
  const canonical = m.path === '/404' ? '' : `<link rel="canonical" href="${esc(url)}" />`;
  const robots = m.noindex ? '<meta name="robots" content="noindex, nofollow" />' : '<meta name="robots" content="index, follow, max-image-preview:large" />';
  const ld = m.noindex ? '' : `<script type="application/ld+json" id="ld-json">${JSON.stringify(jsonLdFor(m.path)).replace(/</g, '\\u003c')}</script>`;
  return [
    `<title>${esc(m.title)}</title>`,
    `<meta name="description" content="${esc(m.description)}" />`,
    robots,
    canonical,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="CIVIK" />`,
    `<meta property="og:locale" content="fr_FR" />`,
    `<meta property="og:title" content="${esc(m.title)}" />`,
    `<meta property="og:description" content="${esc(m.description)}" />`,
    `<meta property="og:url" content="${esc(url)}" />`,
    `<meta property="og:image" content="${OG_IMAGE}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="CIVIK – Ensemble, nous sommes moins seuls." />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(m.title)}" />`,
    `<meta name="twitter:description" content="${esc(m.description)}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE}" />`,
    ld,
  ].filter(Boolean).join('\n    ');
}

/* ── Côté client : mise à jour du <head> lors de la navigation interne ── */
function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

export function applyHead(path) {
  if (typeof document === 'undefined') return;
  const m = metaFor(path);
  const url = `${SITE_URL}${m.path === '/404' ? path : m.path}`;
  if (!m.keepTitle) document.title = m.title;
  setMeta('meta[name="description"]', { name: 'description', content: m.description });
  setMeta('meta[name="robots"]', { name: 'robots', content: m.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large' });
  if (m.noindex) {
    const c = document.head.querySelector('link[rel="canonical"]');
    if (c) c.remove();
  } else {
    setMeta('link[rel="canonical"]', { rel: 'canonical', href: url });
  }
  setMeta('meta[property="og:title"]', { property: 'og:title', content: m.title });
  setMeta('meta[property="og:description"]', { property: 'og:description', content: m.description });
  setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: m.title });
  setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: m.description });
  let ld = document.getElementById('ld-json');
  if (m.noindex) { if (ld) ld.remove(); return; }
  if (!ld) { ld = document.createElement('script'); ld.type = 'application/ld+json'; ld.id = 'ld-json'; document.head.appendChild(ld); }
  ld.textContent = JSON.stringify(jsonLdFor(m.path));
}

export function useHead(path) {
  useEffect(() => { applyHead(path); }, [path]);
}

export const PRERENDER_ROUTES = Object.keys(ROUTES).filter((r) => r !== '/404');
